// Typed contract client. One function per Front-End.md request key. These encode
// the exact endpoint, payload, and which surface (light /api vs compute fast-tier)
// each request uses — the executable mirror of the product doc.
import type {
  DashboardField,
  Folder,
  JobRecord,
  ManifestLayer,
  LayerMetaDocument,
  SignedUpload,
  YieldCommitResult,
  SoilSamplePoint,
  Session,
  MeRecord,
  NotificationCategory,
  NotificationPreference,
  EcPattern,
  FieldCovariate,
  CovariateStackInfo,
  FieldSoilComposition,
  SoilCompositionPointer,
  HaneySamplePoint,
  SampleEvent,
  SampleEventMergeResult,
  FieldJob,
  OrgFieldJob,
  SystemCounts,
  AdminUserRow,
  AdminOrgRow,
  AdminApiKeyRow,
  AdminApiKeyIssued,
  AdminApiKeyImpact,
  AdminApiScope,
  OpsManagerState,
  OpsProcess,
  BoundaryOverlapHit,
  JdOperation,
  OrgMembership,
  OrgMember,
  OrgRole,
  BulkBeginResult,
  BulkErrorRow,
  BulkErrorBadge,
  BulkSessionRow,
  BulkObjectRow,
  BulkConflictRow,
  MergeRecord,
  JdStatus,
  JdProductList,
  JdProductMap,
  JdFieldSend,
  ZoneVersionRead,
} from "../types/contract";
import type { JdSpreadSendResult } from "../types/contract";
import type { Gateway } from "./gateway";
import { ApiError, unwrap } from "./unwrap";
import { contentTypeOf } from "../lib/files";
import { yieldAttachIsLive } from "../lib/yieldLaneLive";
import type { SampleDefaults } from "../lib/sampleDefaults";
import type { ExtractantFactors } from "../lib/extractantFactors";

export { ApiError };

/*
 *
 * BE-Light added it against my "don't silently narrow the scope" point: if it disagrees with the
 * count the panel was showing, that is a bug to surface, not a `changed` -- which keys took effect
 * so copy can name them instead of guessing.
 */
export interface SampleProvenanceResult {
  updated?: number | null;
  changed?: string[] | null;
  changes_derived_values?: boolean | null;
  /*
   *
   * Was the reprocessing actually FILED? "Saved" and "reprocessed" are different claims, and the panel used
   * to render the same state for both. The correction stands either way -- dispatch failures deliberately
   * never fail the edit -- but without this the UI would promise work that was never queued. Only an
   * explicit `false` means "not filed"; absent means an older service that can't answer, which is not the
   * same thing and must not be reported as a failure.
   */
  reprocessing_filed?: boolean | null;
  sample_event_id?: string | null;
  job_id?: string | null;
  depth_normalize_job_id?: string | null;
  recompute_job_id?: string | null;
}

/* * BL-Y5: each 504 is ~11 s inside Light. Cap so a stuck job cannot hang the press forever. */
export const YIELD_COMMIT_MAX_ATTEMPTS = 30;

// Live JD payloads vary in their key names — read the first present string key.
function pickStr(o: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === "string" && v) return v;
  }
  return "";
}

// GUID/opaque-id detector: strings like "4f2a…-…-…" or long cell/digit runs are NOT human names —
// synthesize a label instead of showing them — machine events mus read like "spring '26 tillage"
// never file/operation ids).
export function looksLikeId(s: string): boolean {
  return (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s) ||
    /^[0-9a-f-]{16,}$/i.test(s) ||
    /^\d{6,}$/.test(s)
  );
}

// Human machine-event label from whatever the raw JD operation carries: "Fall '25 harvest (corn)" /
// "Spring '26 tillage" / "Harvest — ".
export function jdOpLabel(
  o: Record<string, unknown>,
  kind: string,
  date?: string,
): string {
  const crop = (() => {
    const direct = pickStr(o, [
      "crop",
      "crop_name",
      "cropName",
      "product",
      "product_name",
      "productName",
      "variety",
    ]);
    if (direct && !looksLikeId(direct)) return direct.toLowerCase();
    const products = o.products;
    if (
      Array.isArray(products) &&
      products.length &&
      typeof products[0] === "object" &&
      products[0] !== null
    ) {
      const p = pickStr(products[0] as Record<string, unknown>, [
        "name",
        "product_name",
        "label",
      ]);
      if (p && !looksLikeId(p)) return p.toLowerCase();
    }
    return "";
  })();
  const d = date ? new Date(date) : null;
  const season =
    d && !Number.isNaN(d.getTime())
      ? `${d.getMonth() >= 7 ? "Fall" : "Spring"} '${String(d.getFullYear()).slice(2)} `
      : "";
  const base = `${season}${kind}${crop ? ` (${crop})` : ""}`;
  return season
    ? base
    : `${kind[0].toUpperCase()}${kind.slice(1)}${crop ? ` (${crop})` : ""}${date ? ` — ${date}` : ""}`;
}

// Lab results attached to a soil-sampling work order. `null` until a file lands.
export interface SamplingOrderResults {
  sample_event_id: string | null;
  properties: unknown;
  samples_written: number | null;
  completed_at: string | null;
}

// What the attach call reports about the work order it just updated. `warnings`
// carries `partial_results` ({matched, expected}) when some rows did not land.
export interface WorkOrderAttach {
  request_id?: string;
  status?: string;
  warnings?: unknown;
}

// Synchronous point-layer lab attach. `missing` is the layer ids with no row.
export interface TargetPointResultsAttach {
  sample_event_id?: string;
  set_id?: string;
  attached?: number;
  already_imported?: boolean;
  properties?: string[];
  work_order?: {
    id?: string;
    status?: string;
    prior_status?: string;
    request_id?: string;
    warnings?: unknown;
  } | null;
  units_unresolved?: unknown;
  extractant_unresolved?: unknown;
  missing?: string[];
}

// A saved soil-sampling work order (SS4). `config` echoes what the order was placed
// with — `points.length` is the count shown on the Work orders page.
export interface SamplingOrder {
  request_id: string;
  status: string;
  created_at: string | null;
  // `lab`: which lab panel + optional Haney test (every sample / 1-of-N) was ordered.
  config?: {
    kind?: string;
    points?: unknown[];
    cores?: { lng: number; lat: number; zone: number }[];
    zone_version_id?: string;
    lab?: import("../lib/labTests").LabOrderConfig;
  } | null;
  // The design the order was placed against (`points:<setId>` / `zones:<versionId>`).
  design_event_id: string | null;
  results: SamplingOrderResults | null;
}

function wireText(v: unknown): string | null {
  return typeof v === "string" && v !== "" ? v : null;
}

function samplingResultsFromWire(v: unknown): SamplingOrderResults | null {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  return {
    sample_event_id: wireText(r.sample_event_id),
    properties: "properties" in r ? r.properties : null,
    samples_written:
      typeof r.samples_written === "number" ? r.samples_written : null,
    completed_at: wireText(r.completed_at),
  };
}

export function samplingOrderFromWire(
  r: Record<string, unknown>,
  fieldId: string,
  index: number,
): SamplingOrder {
  return {
    request_id: String(
      r.soil_sampling_request_id ??
        r.request_id ??
        r.id ??
        `${fieldId}-order-${index}`,
    ),
    status: String(r.status ?? "submitted"),
    created_at:
      typeof r.created_at === "string"
        ? r.created_at
        : typeof r.ordered_at === "string"
          ? r.ordered_at
          : null,
    config: (r.config ?? null) as SamplingOrder["config"],
    design_event_id: wireText(r.design_event_id) ?? wireText(r.design_id),
    results: samplingResultsFromWire(r.results),
  };
}

// A saved EC-scan work order (EC2) — `pattern` records the scan type ordered.
export interface EcScanOrder {
  request_id: string;
  status: string;
  created_at: string | null;
  pattern: string | null;
}

// A list endpoint whose envelope we inferred from prose rather than a pasted body: BE-Light's admin reads
// are documented as e.g. "every ACTIVE job system-wide... plus counts by status", which is an OBJECT, not
// the bare array an earlier draft assumed -- and `(data ?? ).filter` on an object threw, taking the WHOLE
// System Dashboard down with it despite panels being designed to fail independently. Normalising at the
// boundary means a shape surprise costs that panel its contents, never the page.
function asList<T>(v: unknown, ...keys: string[]): T[] {
  if (Array.isArray(v)) return v as T[];
  if (v && typeof v === "object") {
    for (const k of keys) {
      const inner = (v as Record<string, unknown>)[k];
      if (Array.isArray(inner)) return inner as T[];
    }
  }
  return [];
}

// Hand-typed. Switch to the generated schema type after #580 merges.
export interface DeletedSampleResults {
  deleted_rows: number;
  cleared_rows: number;
  event_id: string;
  nutrient: string | null;
  cascade_job_ids: string[];
}

// A status under 100 is a transport failure (no HTTP response), not an answer.
function responseStatus(err: unknown): number | undefined {
  if (!err || typeof err !== "object") return undefined;
  const record = err as {
    status?: unknown;
    response?: { status?: unknown };
  };
  const status =
    typeof record.response?.status === "number"
      ? record.response.status
      : record.status;
  if (typeof status !== "number" || status < 100) return undefined;
  return status;
}

function postedStatus(env: object): number | null {
  if (!("httpStatus" in env)) return null;
  const status = (env as { httpStatus?: unknown }).httpStatus;
  return typeof status === "number" && status >= 100 ? status : null;
}

export type PasswordResetCompleteResult =
  { ok: true } | { ok: false; status: number | null };

export function makeClient(gw: Gateway) {
  return {
    // ---- Auth (Front-End.md §A7) --------------------------------------------
    /*
     *
     * `joinCode`: when present the server validates it FIRST and, if good, adds the user to THAT
     * org instead of creating a default one — atomically, so a rotated/deleted code can't leave
     * someone holding an org they never asked for.
     */
    /*
     *
     * BE-Light showed the assumption was impossible: their model **ignores unknown fields** so a
     * build predating `ui_mode` would have silently DROPPED it, never refused it — no version of
     * "not deployed yet" could produce that symptom.
     */
    async signup(
      joinCode: string | undefined,
      uiMode: "easy" | "advanced",
    ): Promise<Pick<Session, "user_id" | "email" | "orgs" | "role">> {
      const body: Record<string, unknown> = { ui_mode: uiMode };
      if (joinCode) body.join_code = joinCode;
      return unwrap(
        await gw.apiPost<Pick<Session, "user_id" | "email" | "orgs" | "role">>(
          "/auth/signup",
          body,
        ),
      );
    },
    /*
     *
     * Pre-flight: is this join code usable, and whose org is it? UNAUTHENTICATED by necessity (there's no
     * account yet), so it is ADVISORY ONLY — the atomic guarantee lives in signup, which does the lookup
     * INSIDE the insert (BE-Light `sm-api-read-00117-xgn`).
     *
     * An invalid code is a **200 with `valid:false`**, never a 404 — it's a form-field answer, not a
     * missing resource. So ANY thrown error here (endpoint absent, offline, 5xx) means "couldn't check"
     * and returns null; it must NEVER be reported as an invalid code. An earlier draft mapped
     * `ERR_NOT_FOUND` to `{valid:false}`, which would have told a user with a perfectly good code that it
     * was wrong the moment this endpoint was missing from an environment.
     */
    async checkJoinCode(
      joinCode: string,
    ): Promise<{ valid: boolean; org_name?: string | null } | null> {
      try {
        return unwrap(
          await gw.apiPost<{ valid: boolean; org_name?: string | null }>(
            "/auth/join-code:check",
            { join_code: joinCode },
          ),
        );
      } catch {
        return null;
      }
    },
    async refresh(refreshToken: string): Promise<Session> {
      return unwrap(
        await gw.apiPost<Session>("/auth/refresh", {
          refresh_token: refreshToken,
        }),
      );
    },
    /* * Identity + `is_global_admin`, re-read rather than trusted from the login snapshot (see MeRecord). */
    async fetchMe(): Promise<MeRecord> {
      return unwrap(await gw.apiGet<MeRecord>("/users/me"));
    },
    async logout(): Promise<void> {
      let refreshToken: string | null;
      try {
        refreshToken = localStorage.getItem("sm_refresh") || null;
      } catch {
        refreshToken = null;
      }
      const env = await gw.apiPost<{ logged_out?: boolean }>(
        "/auth/logout",
        refreshToken ? { refresh_token: refreshToken } : {},
      );
      const error = Array.isArray(env.error) ? env.error[0] : env.error;
      if (error) throw new ApiError(error.code, error.message, error.details);
    },
    // Any HTTP answer is the same outcome. The screen must not separate a
    // missing account, a rate limit, or a missing route from an accepted
    // request. Only a call that never received a response is a failure.
    async requestPasswordReset(email: string): Promise<void> {
      try {
        await gw.apiPost<{ accepted: boolean }>("/auth/password-reset", {
          email: email.trim(),
        });
      } catch (err) {
        if (responseStatus(err) !== undefined) return;
        throw err;
      }
    },
    // The address is the one the reset link named. The route reads the
    // revocation instant from Firebase and pads every answer, so this waits
    // for that answer. The result carries a status only: the response body
    // is dropped so a caller cannot log it.
    async completePasswordReset(
      email: string,
    ): Promise<PasswordResetCompleteResult> {
      try {
        const env = await gw.apiPost<{ accepted: boolean }>(
          "/auth/password-reset/complete",
          { email: email.trim() },
        );
        const status = postedStatus(env);
        if (env.error != null || (status !== null && status >= 400))
          return { ok: false, status };
        return { ok: true };
      } catch (err) {
        return { ok: false, status: responseStatus(err) ?? null };
      }
    },

    async listMyOrgs(): Promise<OrgMembership[]> {
      return unwrap(await gw.apiGet<OrgMembership[]>("/orgs"));
    },
    // Join an org by pasting its join code → member; returns the joined org + the updated session.
    async joinOrg(joinCode: string): Promise<{
      org: OrgMembership;
      already_member?: boolean;
    }> {
      return unwrap(
        await gw.apiPost<{
          org: OrgMembership;
          already_member?: boolean;
        }>("/orgs/join", { join_code: joinCode }),
      );
    },
    async leaveOrg(orgId: string): Promise<void> {
      unwrap(await gw.apiDelete<{ left: string }>(`/orgs/${orgId}/membership`));
    },
    async createOrg(name: string): Promise<{ org: OrgMembership }> {
      return unwrap(
        await gw.apiPost<{ org: OrgMembership }>("/orgs", { name }),
      );
    },
    // Rotate an org's join code (admin only) — never evicts existing members.
    async rotateJoinCode(orgId: string): Promise<{ join_code: string }> {
      const r = unwrap(
        await gw.apiPost<{ org_id: string; join_code: string }>(
          `/orgs/${orgId}/join-code:rotate`,
          {},
        ),
      );
      return { join_code: r.join_code };
    },
    // Rename an org (admin only). NOTE: not in the BE-light contract yet — `Back-End-Light__org-rename`
    // filed; works in mock, 404s live until they add it (surfaces as an error note in Manage).
    async renameOrg(orgId: string, name: string): Promise<OrgMembership> {
      return unwrap(
        await gw.apiPatch<OrgMembership>(`/orgs/${orgId}`, { name }),
      );
    },
    // Admin: list an org's members.
    async listOrgMembers(orgId: string): Promise<OrgMember[]> {
      return unwrap(await gw.apiGet<OrgMember[]>(`/orgs/${orgId}/members`));
    },
    // Admin: remove a member → { removed, session? } (session present only when removing MYSELF).
    async removeOrgMember(
      orgId: string,
      userId: string,
    ): Promise<{ removed: string }> {
      return unwrap(
        await gw.apiDelete<{ removed: string }>(
          `/orgs/${orgId}/members/${userId}`,
        ),
      );
    },
    // Admin: change a member's role (member↔admin) → { user_id, role, session? } (session on self-change).
    async setOrgMemberRole(
      orgId: string,
      userId: string,
      role: OrgRole,
    ): Promise<{ user_id: string; role: OrgRole }> {
      return unwrap(
        await gw.apiPatch<{
          user_id: string;
          role: OrgRole;
        }>(`/orgs/${orgId}/members/${userId}`, { role }),
      );
    },

    // ---- Create a field from a boundary source (Front-End.md §A1) -----------
    // Draw + inline GeoJSON: one create-with-boundary call.
    async createFieldFromBoundary(
      name: string,
      boundary_geojson: unknown,
    ): Promise<DashboardField> {
      return unwrap(
        await gw.apiPost<DashboardField>(
          "/fields",
          { name, boundary_geojson },
          { activeOrg: true },
        ),
      );
    },
    // Uploaded boundary file (shp-zip / kml / kmz): create field, then mint + PUT the file
    // (async parses the boundary). For.geojson the caller parses + uses createFieldFromBoundary.
    async createFieldFromFile(
      name: string,
      file: File,
    ): Promise<DashboardField> {
      const field = unwrap(
        await gw.apiPost<DashboardField>(
          "/fields",
          { name },
          { activeOrg: true },
        ),
      );
      const contentType = contentTypeOf(file); // one canonical value for both mint + PUT
      const mint = unwrap(
        await gw.apiPost<SignedUpload>(
          `/fields/${field.field_id}/boundary:upload`,
          {
            content_type: contentType,
          },
          { activeOrg: true },
        ),
      );
      await gw.uploadToSignedUrl(mint.signed_put_url, file, contentType);
      return field;
    },

    // ---- Rename a field (Front-End.md §A9) — BE-light control-plane write ----
    async renameField(fieldId: string, name: string): Promise<DashboardField> {
      // BE-light exposes rename as PATCH /api/fields/{id} (F8) — NOT PUT (405). Field-move
      // (F8b) stays PUT /fields/{id}/folder. (Was the one PUT missed in the PATCH migration.)
      return unwrap(
        await gw.apiPatch<DashboardField>(
          `/fields/${fieldId}`,
          { name },
          { activeOrg: true },
        ),
      );
    },

    // ---- Folders — org-scoped nesting tree (Front-End.md §A10) ---------------
    async fetchFolders(): Promise<Folder[]> {
      return unwrap(
        await gw.apiGet<Folder[]>("/folders", {}, { activeOrg: true }),
      );
    },
    async createFolder(
      name: string,
      parentFolderId: string | null = null,
    ): Promise<Folder> {
      return unwrap(
        await gw.apiPost<Folder>(
          "/folders",
          {
            name,
            parent_folder_id: parentFolderId,
          },
          { activeOrg: true },
        ),
      );
    },
    // BE-light serves folder updates as PATCH {name?, parent_folder_id?} (rename + reparent
    // folded into one partial update). Front-End.md §A10.
    async renameFolder(folderId: string, name: string): Promise<Folder> {
      return unwrap(
        await gw.apiPatch<Folder>(
          `/folders/${folderId}`,
          { name },
          { activeOrg: true },
        ),
      );
    },
    async reparentFolder(
      folderId: string,
      parentFolderId: string | null,
    ): Promise<Folder> {
      return unwrap(
        await gw.apiPatch<Folder>(
          `/folders/${folderId}`,
          {
            parent_folder_id: parentFolderId,
          },
          { activeOrg: true },
        ),
      );
    },
    async deleteFolder(folderId: string): Promise<void> {
      unwrap(
        await gw.apiDelete<null>(`/folders/${folderId}`, { activeOrg: true }),
      );
    },
    // Move a field into a folder (or Unfiled: folder_id=null) — single control-plane write.
    async moveField(
      fieldId: string,
      folderId: string | null,
    ): Promise<DashboardField> {
      return unwrap(
        await gw.apiPut<DashboardField>(
          `/fields/${fieldId}/folder`,
          {
            folder_id: folderId,
          },
          { activeOrg: true },
        ),
      );
    },
    // Permanently delete a field and ALL its server-side data memory of them from the DB and
    // everything else") — cascading DELETE, cannot be undone. BE-light F10: DB cascade + GCS
    // artifact cleanup; mock deletes locally.
    async checkBoundaryOverlap(body: {
      geojson?: unknown;
      field_id?: string;
      min_overlap_pct?: number;
    }): Promise<BoundaryOverlapHit[]> {
      return unwrap(
        await gw.apiPost<BoundaryOverlapHit[]>(
          "/fields/boundary:check-overlap",
          body,
          { activeOrg: true },
        ),
      );
    },
    /*
     *
     * `` is a real "in no org field" answer, never an error.
     */
    async locateFields(
      points: { lon: number; lat: number }[],
    ): Promise<{ matches: { field_id: string; field_name: string }[] }[]> {
      const out: { matches: { field_id: string; field_name: string }[] }[] = [];
      for (let at = 0; at < points.length; at += 1000) {
        const res = unwrap(
          await gw.apiPost<{
            results: { matches: { field_id: string; field_name: string }[] }[];
          }>(
            "/fields:locate",
            { points: points.slice(at, at + 1000) },
            { activeOrg: true },
          ),
        );
        out.push(...(res.results ?? []));
      }
      return out;
    },
    /*
     *
     * 404 until that door is live — callers must treat absence as "unknown", never as "none".
     */
    async fetchMachineOperations(fieldId: string): Promise<{
      operations: {
        op_id: string;
        jd_operation_id: string | null;
        kind: string | null;
        op_date: string | null;
        crop: string | null;
        crop_source: string | null;
        source: string | null;
        field_operation_id: string | null;
        attestation: Record<string, unknown> | null;
        point_count: number | null;
        content_version: string | null;
        updated_at: string | null;
      }[];
      stack: {
        content_version: string;
        cell_count: number;
        is_stale: boolean;
        has_machine_data: boolean;
        updated_at: string | null;
      } | null;
    }> {
      return unwrap(
        await gw.apiGet(
          `/fields/${fieldId}/machine-operations`,
          {},
          { activeOrg: true },
        ),
      );
    },
    /*
     *
     * BL-Y5 — wait on the already-filed `yield_attach` job after the bytes are in GCS.
     * Does not re-file and does not mint. A 504 `ERR_TIMEOUT` retries the **same body**.
     */
    async commitFieldYield(
      fieldId: string,
      body: { object_path: string; job_id?: string },
    ): Promise<YieldCommitResult> {
      const payload: { object_path: string; job_id?: string } = {
        object_path: body.object_path,
      };
      if (body.job_id) payload.job_id = body.job_id;
      let last: ApiError | null = null;
      for (let i = 0; i < YIELD_COMMIT_MAX_ATTEMPTS; i++) {
        const env = await gw.apiPost<YieldCommitResult>(
          `/fields/${fieldId}/yield:commit`,
          payload,
          { activeOrg: true },
        );
        if (!env.error) return env.data as YieldCommitResult;
        const e = Array.isArray(env.error) ? env.error[0]! : env.error;
        last = new ApiError(e.code, e.message, e.details);
        if (e.code !== "ERR_TIMEOUT") throw last;
      }
      throw (
        last ??
        new ApiError(
          "ERR_TIMEOUT",
          "the yield attach did not finish in time; retry this commit — the lane is still running and this door did not treat it as landed",
        )
      );
    },
    /*
     *
     * BL-Y2 — per-field "Add data → Upload yield map". Files the lane BEFORE the PUT URL is
     * minted (same orphaned-byte guarantee as `:begin`). `fe_yield` is required on the door
     * this client also refuses to call it until `yieldAttachIsLive`. After the PUT, BL-Y5
     * waits on that job (`commit` is null if the wait exhausted or the door refused).
     */
    async uploadFieldYield(
      fieldId: string,
      file: File,
      feYield: { envelope: unknown; field_id?: string },
    ): Promise<{
      job_id: string | null;
      object_path: string;
      commit: YieldCommitResult | null;
    }> {
      if (!yieldAttachIsLive()) {
        throw new Error(
          "Yield attach is not live yet — compute and BE-Light must both be deployed first.",
        );
      }
      const contentType = contentTypeOf(file);
      const env = await gw.apiPost<SignedUpload & { job_id?: string | null }>(
        `/fields/${fieldId}/yield:upload`,
        { content_type: contentType, filename: file.name, fe_yield: feYield },
        { activeOrg: true },
      );
      const data = unwrap(env);
      const mint = { ...data, job_id: data.job_id ?? env.meta?.job_id ?? null };
      await gw.uploadToSignedUrl(mint.signed_put_url, file, contentType);
      let commit: YieldCommitResult | null = null;
      try {
        commit = await this.commitFieldYield(fieldId, {
          object_path: mint.object_path,
          ...(mint.job_id ? { job_id: mint.job_id } : {}),
        });
      } catch {
        // PUT landed and the lane is filed — BL-Y4 still runs. Leave the press-time preview.
      }
      return {
        job_id: mint.job_id ?? null,
        object_path: mint.object_path,
        commit,
      };
    },
    /*
     *
     * A COMBINED field answers the FIRST call with 409 `ERR_CONFIRM_REQUIRED` and `details =
     * [{field_id, name}]` of every field that would go with it (chained merges included) the SECOND
     * call with `confirmMerged` deletes the whole tree in one transaction (BE-Light ruling that
     * a combined field must be deletable). `ERR_DELETE_REFUSED` is a different code -- a HARD
     * refusal that must never get a "delete anyway" -- and is left to surface as an ordinary
     * failure.
     */
    async deleteField(
      fieldId: string,
      opts: { confirmMerged?: boolean } = {},
    ): Promise<void> {
      unwrap(
        await gw.apiDelete<null>(
          `/fields/${fieldId}${opts.confirmMerged ? "?confirm_merged=1" : ""}`,
          { activeOrg: true },
        ),
      );
    },

    // Connection status (BE-light JD0) — always 200; the authoritative check.
    // `configured` = JD integration wired server-side; `connected` = this user has a token.
    /*
     *
     * Adding a scope does NOT invalidate the token: it keeps refreshing and looks perfectly valid
     * and only the calls needing the new scope fail — as a 403 indistinguishable from a revoked
     * connection.
     */
    async jdStatus(): Promise<JdStatus> {
      return unwrap(
        await gw.apiGet<JdStatus>(
          "/integrations/johndeere/status",
          {},
          { activeOrg: true },
        ),
      );
    },
    async jdOrgs(): Promise<{ jd_org_id: string; name: string }[]> {
      return unwrap(
        await gw.apiGet<{ jd_org_id: string; name: string }[]>(
          "/integrations/johndeere/orgs",
          {},
          { activeOrg: true },
        ),
      );
    },
    // `linked_field_id`/`linked_field_name` — BE-Light rev 00099: non-null when this JD field is
    // ALREADY linked to one of our fields, so the bulk picker can say "already in — Home 80"
    // instead of creating a duplicate.
    /*
     *
     * `product_type` is load-bearing, not decoration: the id alone does not say which collection a
     * product lives in, so a Custom blend mapped without it would resolve to nothing at send time.
     * `archived` is FLAGGED, not filtered — don't offer one as a NEW mapping, but keep resolving
     * it, or an existing mapping to a retired product becomes unexplainable.
     */
    /*
     *
     * It carries the failure I asked the store to make detectable: a product renamed or replaced in
     * Ops Center would otherwise be sent under a name that still looks right. `jd_product_name` (as
     * mapped) and `live_name` (now) come back together so the drift is VISIBLE rather than
     * inferred.
     */
    async jdProductMap(jdOrgId: string): Promise<JdProductMap> {
      return unwrap(
        await gw.apiGet<JdProductMap>(
          "/integrations/johndeere/product-map",
          {
            jd_org_id: jdOrgId,
          },
          { activeOrg: true },
        ),
      );
    },
    /*
     *
     * SEND `jd_product_name`: it is optional on the wire but the drift check is BLIND without it so
     * a mapping saved without a name can never be checked again.
     */
    async saveJdProductMap(body: {
      jd_org_id: string;
      our_product: string;
      jd_product_id: string;
      jd_product_type: string;
      jd_product_name?: string | null;
    }): Promise<void> {
      unwrap(
        await gw.apiPut("/integrations/johndeere/product-map", body, {
          activeOrg: true,
        }),
      );
    },
    /*
     *
     * That is `jdSendGate`'s job: `ready` supplies the link, `ambiguous` makes the user pick, and
     * this call never guesses. The body is the zip ITSELF (`buildJdRxZips` output), relayed whole —
     * BE-Light validates only zip magic and a 20 MB cap; the artifact contract stays FE-side where
     * the writer lives.
     */
    async sendJdSpreadMap(
      fieldId: string,
      args: {
        jdOrgId: string;
        jdFieldId: string;
        ourProduct: string;
        filename?: string;
        rxId?: string;
      },
      zip: Uint8Array,
    ): Promise<JdSpreadSendResult> {
      return unwrap(
        await gw.apiPostBinary<JdSpreadSendResult>(
          `/fields/${fieldId}/johndeere/spread-map:send`,
          zip,
          {
            jd_org_id: args.jdOrgId,
            jd_field_id: args.jdFieldId,
            our_product: args.ourProduct,
            ...(args.filename ? { filename: args.filename } : {}),
            ...(args.rxId ? { rx_id: args.rxId } : {}),
          },
          { activeOrg: true },
        ),
      );
    },
    /*
     *
     * Exact-or-fail on BOTH sides: the FE verifies set-equality before calling, and the server
     * re-verifies (422 names both directions, all-or-nothing insert even under a concurrent edit).
     */
    async attachZoneResults(
      fieldId: string,
      zoneVersionId: string,
      rows: { zone: number; properties: Record<string, unknown> }[],
      opts?: { work_order_id?: string },
    ): Promise<{
      attached?: number;
      sample_event_id?: string;
      has_results?: boolean;
      work_order?: WorkOrderAttach;
    }> {
      const body: {
        rows: { zone: number; properties: Record<string, unknown> }[];
        work_order_id?: string;
      } = { rows };
      // Absent on the Visualize upload: an older bundle must keep sending `{rows}` only.
      if (opts?.work_order_id) body.work_order_id = opts.work_order_id;
      return unwrap(
        await gw.apiPost<{
          attached?: number;
          sample_event_id?: string;
          has_results?: boolean;
          work_order?: WorkOrderAttach;
        }>(
          `/fields/${fieldId}/sample-events/zones:${zoneVersionId}/results`,
          body,
          { activeOrg: true },
        ),
      );
    },
    /*
     * Rows the point-layer preview already matched. The server checks the ids
     * and stores these values. It does not read the sheet's headers again.
     */
    async attachTargetPointResultRows(
      fieldId: string,
      setId: string,
      rows: {
        sample_id: string;
        properties: Record<string, unknown>;
        raw?: Record<string, string | null>;
      }[],
      opts?: { workOrderId?: string; defaults?: SampleDefaults },
    ): Promise<TargetPointResultsAttach> {
      const body: {
        rows: {
          sample_id: string;
          properties: Record<string, unknown>;
          raw?: Record<string, string | null>;
        }[];
        work_order_id?: string;
        sample_defaults?: SampleDefaults;
      } = { rows };
      if (opts?.workOrderId) body.work_order_id = opts.workOrderId;
      if (opts?.defaults) body.sample_defaults = opts.defaults;
      return unwrap(
        await gw.apiPost<TargetPointResultsAttach>(
          `/fields/${fieldId}/soil-sampling/target-points/${encodeURIComponent(setId)}/result-rows`,
          body,
          { activeOrg: true },
        ),
      );
    },
    /*
     * Raw CSV attach. The point-layer screen does not call this: it posts the
     * rows it already matched. The route stays for the API contract.
     * `sample_defaults` is one JSON query value, sent only when the caller has
     * a complete depth / extractant / units set.
     */
    async attachTargetPointResults(
      fieldId: string,
      setId: string,
      file: File,
      opts?: { workOrderId?: string; defaults?: SampleDefaults },
    ): Promise<TargetPointResultsAttach> {
      const params: Record<string, string> = {
        source_filename: file.name,
      };
      if (opts?.workOrderId) params.work_order_id = opts.workOrderId;
      if (opts?.defaults)
        params.sample_defaults = JSON.stringify(opts.defaults);
      const bytes = new Uint8Array(await file.arrayBuffer());
      return unwrap(
        await gw.apiPostBinary<TargetPointResultsAttach>(
          `/fields/${fieldId}/soil-sampling/target-points/${encodeURIComponent(setId)}/results`,
          bytes,
          params,
          { activeOrg: true },
          "text/csv",
        ),
      );
    },
    /*
     *
     * The feature contract is IDENTICAL to the manifest `zones` layer, deliberately: saved
     * properties (decile `code` included) survive under the server-computed `zone`/`area_acres`, so
     * a picked old version rides the exact render/classify path the active layer already uses.
     */
    async fetchZoneVersionGeojson(
      fieldId: string,
      zoneVersionId: string,
    ): Promise<GeoJSON.FeatureCollection | null> {
      const r = unwrap(
        await gw.apiGet<ZoneVersionRead>(
          `/fields/${fieldId}/zones/${zoneVersionId}`,
          {},
          { activeOrg: true },
        ),
      );
      return r.geojson ?? null;
    },
    /* * Z1b with its metadata — `is_active` is what tells the sidebar which saved design is the field's zones layer. */
    async fetchZoneVersion(
      fieldId: string,
      zoneVersionId: string,
    ): Promise<ZoneVersionRead> {
      return unwrap(
        await gw.apiGet<ZoneVersionRead>(
          `/fields/${fieldId}/zones/${zoneVersionId}`,
          {},
          { activeOrg: true },
        ),
      );
    },
    /*
     *
     * Delete a saved sampling DESIGN by its SS8 event id (`points:<set_id>` / `zones:<zone_version_id>`) —
     * BE-Light SS8b, design-only (attributed results preserved), idempotent.
     */
    async deleteSampleEvent(fieldId: string, eventId: string): Promise<void> {
      unwrap(
        await gw.apiDelete<{ deleted?: boolean }>(
          `/fields/${fieldId}/sample-events/${encodeURIComponent(eventId)}`,
          { activeOrg: true },
        ),
      );
    },
    // Hand-typed beside deleteSampleEvent. Only an absent nutrient deletes
    // that one event's rows. A blank is sent as-is so the route answers 422;
    // it must never widen into the irreversible delete-all.
    async deleteSampleResults(
      fieldId: string,
      eventId: string,
      nutrient?: string,
    ): Promise<DeletedSampleResults> {
      const query =
        nutrient === undefined
          ? ""
          : `?nutrient=${encodeURIComponent(nutrient)}`;
      return unwrap(
        await gw.apiDelete<DeletedSampleResults>(
          `/fields/${fieldId}/sample-events/${encodeURIComponent(eventId)}/results${query}`,
          { activeOrg: true },
        ),
      );
    },
    /* * A field's send history, newest first — the "Lime sent to JD Ops 8/11" line and the re-send warning. */
    async jdFieldSends(fieldId: string): Promise<JdFieldSend[]> {
      return unwrap(
        await gw.apiGet<JdFieldSend[]>(
          `/fields/${fieldId}/johndeere/sends`,
          {},
          { activeOrg: true },
        ),
      );
    },
    async jdProducts(jdOrgId: string): Promise<JdProductList> {
      return unwrap(
        await gw.apiGet<JdProductList>(
          "/integrations/johndeere/products",
          {
            jd_org_id: jdOrgId,
          },
          { activeOrg: true },
        ),
      );
    },
    async jdFields(jdOrgId: string): Promise<
      {
        jd_org_id: string;
        jd_field_id: string;
        name: string;
        linked_field_id?: string | null;
        linked_field_name?: string | null;
      }[]
    > {
      return unwrap(
        await gw.apiGet<
          {
            jd_org_id: string;
            jd_field_id: string;
            name: string;
            linked_field_id?: string | null;
            linked_field_name?: string | null;
          }[]
        >(
          "/integrations/johndeere/fields",
          { jd_org_id: jdOrgId },
          { activeOrg: true },
        ),
      );
    },
    // /connect returns JSON `{authorize_url}` (it does NOT redirect) — open that in the popup.
    async jdAuthorizeUrl(): Promise<string> {
      const r = unwrap(
        await gw.apiGet<{ authorize_url: string }>(
          "/integrations/johndeere/connect",
          {},
          { activeOrg: true },
        ),
      );
      return r.authorize_url;
    },
    // Disconnect / de-link the JD Ops account (JD1b).
    async jdDisconnect(): Promise<void> {
      await gw.apiDelete("/integrations/johndeere/connect", {
        activeOrg: true,
      });
    },
    // Mock/dev only: simulate the JD OAuth completing.
    async jdSimulateConnect(): Promise<void> {
      await gw.apiPost(
        "/integrations/johndeere/connect",
        {},
        { activeOrg: true },
      );
    },
    async createFieldFromJd(
      jdOrgId: string,
      jdFieldId: string,
      name?: string,
    ): Promise<DashboardField> {
      return unwrap(
        await gw.apiPost<DashboardField>(
          "/fields/from-johndeere",
          {
            jd_org_id: jdOrgId,
            jd_field_id: jdFieldId,
            ...(name ? { name } : {}),
          },
          { activeOrg: true },
        ),
      );
    },
    // Link an EXISTING field to a JD Ops field (BE-light JD4) — prerequisite for
    // pulling JD data into a field that wasn't created from JD. Front-End.md §A1.
    async linkJdField(
      fieldId: string,
      jdOrgId: string,
      jdFieldId: string,
    ): Promise<DashboardField> {
      return unwrap(
        await gw.apiPost<DashboardField>(
          `/fields/${fieldId}/link-johndeere`,
          {
            jd_org_id: jdOrgId,
            jd_field_id: jdFieldId,
          },
          { activeOrg: true },
        ),
      );
    },

    // ---- Reads (light /api) -------------------------------------------------
    async fetchFields(): Promise<DashboardField[]> {
      return unwrap(
        await gw.apiGet<DashboardField[]>("/fields", {}, { activeOrg: true }),
      );
    },
    // report branding —; server side = `Back-End-Light__report-branding` One GET carries the whole
    // picture: the active org's branding, the user's personal branding and which one the user chose
    // ('org' is the default).
    async fetchBranding(orgId: string): Promise<{
      org: { name: string; address: string; logo: string | null } | null;
      personal: { name: string; address: string; logo: string | null } | null;
      use: "org" | "personal";
    }> {
      return unwrap(
        await gw.apiGet(`/branding`, { org_id: orgId }, { activeOrg: true }),
      );
    },
    async savePersonalBranding(branding: {
      name: string;
      address: string;
      logo: string | null;
    }): Promise<void> {
      unwrap(
        await gw.apiPut(`/branding/personal`, branding, { activeOrg: true }),
      );
    },
    async saveOrgBranding(
      orgId: string,
      branding: { name: string; address: string; logo: string | null },
    ): Promise<void> {
      unwrap(await gw.apiPut(`/orgs/${orgId}/branding`, branding));
    },
    // ORG DEFAULT SOIL UNITS: the org's default display lens for members who have never chosen
    // their own. GET is member-readable; PUT is org_admin-only — ENFORCED SERVER-SIDE (the FE gate
    // is convenience).
    async fetchOrgSoilUnits(
      orgId: string,
    ): Promise<Record<string, unknown> | null> {
      return unwrap(
        await gw.apiGet<Record<string, unknown> | null>(
          `/orgs/${orgId}/soil-units`,
        ),
      );
    },
    async updateOrgSoilUnits(
      orgId: string,
      units: {
        depth_in: number;
        p_extractant: string;
        k_extractant: string;
        mass_basis: string;
      } | null,
    ): Promise<void> {
      unwrap(await gw.apiPut(`/orgs/${orgId}/soil-units`, { units }));
    },
    async saveBrandingUse(use: "org" | "personal"): Promise<void> {
      unwrap(
        await gw.apiPut(`/branding/preference`, { use }, { activeOrg: true }),
      );
    },

    // Pre-flight: DB's rules, BE-Light's tenancy.
    async mergeFieldsPreflight(fieldIds: string[]): Promise<{
      mergeable: boolean;
      reasons: { reason?: string; code?: string; detail?: string }[];
    }> {
      return unwrap(
        await gw.apiPost(
          `/fields:merge-preflight`,
          { field_ids: fieldIds },
          { activeOrg: true },
        ),
      );
    },
    /*
     *
     * IRREVERSIBLE, and the server re-evaluates the gate at authorise time rather than trusting the
     * pre-flight — between the button rendering and this call a sample can land or a job can start
     * so a 4xx here is a legitimate answer that must be SHOWN. A merge MINTS A NEW FIELD, and
     * nothing in the system can name it: the merge record holds the sources only, and no rule picks
     * fairly between "Home 80" and "Home 40".
     */
    async mergeFields(body: {
      field_ids: string[];
      name: string;
      folder_id?: string | null;
    }): Promise<MergeRecord & { job_id?: string }> {
      const env = await gw.apiPost<MergeRecord>(`/fields:merge`, body, {
        activeOrg: true,
      });
      return { ...unwrap(env), job_id: env.meta?.job_id ?? undefined };
    },
    /*
     *
     * Never render it as the headline; `MergeWatchStrip` keeps it behind a details toggle.
     */
    async mergeStatus(mergeId: string): Promise<MergeRecord> {
      return unwrap(
        await gw.apiGet<MergeRecord>(
          `/fields/merges/${mergeId}`,
          {},
          { activeOrg: true },
        ),
      );
    },

    /*
     *
     * The frames arrive as ONE additive row — `{id:"animation-frames", kind:"animation"
     * default_visible:false, inline:{nx, ny, mask, frames}}` — a distinct `kind` rather than a
     * renderable layer, so a naive renderer walking every row cannot try to draw it. THE ROW CAN BE
     * ABSENT FOR TWO REASONS AND THEY LOOK IDENTICAL: not generated yet, or over BE-Light's **2 MB
     * inline budget**, in which case it is omitted rather than taking the whole manifest read down
     * with it.
     */
    async fetchFieldLayers(
      fieldId: string,
      opts: { includeAnimation?: boolean } = {},
    ): Promise<ManifestLayer[]> {
      // `rx-planes` is RETIRED, not forgotten: the lime Rx animation now paints from the send the
      // token is what lets BE-Light drop it without a shipped caller 404ing.
      return unwrap(
        await gw.apiGet<ManifestLayer[]>(
          `/fields/${fieldId}/layers`,
          opts.includeAnimation ? { include: "animation" } : undefined,
          { activeOrg: true },
        ),
      );
    },
    // The layer list without payloads. A matching ETag comes back as
    // `notModified` and the caller keeps the document it already has.
    async fetchFieldLayerList(
      fieldId: string,
      opts: {
        includeAnimation?: boolean;
        ifNoneMatch?: string;
        signal?: AbortSignal;
      } = {},
    ): Promise<{
      notModified: boolean;
      etag: string | null;
      document: LayerMetaDocument | null;
    }> {
      const params: Record<string, string> = { view: "meta" };
      if (opts.includeAnimation) params.include = "animation";
      const env = await gw.apiGet<LayerMetaDocument>(
        `/fields/${fieldId}/layers`,
        params,
        { activeOrg: true },
        {
          headers: opts.ifNoneMatch
            ? { "If-None-Match": opts.ifNoneMatch }
            : undefined,
          signal: opts.signal,
          acceptNotModified: true,
        },
      );
      if (env.httpStatus === 304) {
        return { notModified: true, etag: env.etag ?? null, document: null };
      }
      return {
        notModified: false,
        etag: env.etag ?? null,
        document: unwrap(env),
      };
    },
    // One layer, same shape as its row on the full list. A timeseries layer
    // is the step manifest plus the default step.
    async fetchLayerDetail(
      fieldId: string,
      layerId: string,
      opts: { signal?: AbortSignal } = {},
    ): Promise<ManifestLayer> {
      return unwrap(
        await gw.apiGet<ManifestLayer>(
          `/fields/${fieldId}/layers/${encodeURIComponent(layerId)}`,
          {},
          { activeOrg: true },
          { signal: opts.signal },
        ),
      );
    },
    // CV1 — the full categorized covariate catalog for the field's assembled stack (up to
    // ~1,500). Light reads it from the same DB view async writes (v_field_covariates). §A4.
    async fetchFieldCovariates(fieldId: string): Promise<FieldCovariate[]> {
      return unwrap(
        await gw.apiGet<FieldCovariate[]>(
          `/fields/${fieldId}/covariates`,
          {},
          { activeOrg: true },
        ),
      );
    },
    // Materialize ONE covariate as a map-renderable layer (relays DB C1 → COG/gridded).
    // Returns a ManifestLayer the map renders through the same pipeline as /layers. §A4.
    async fetchCovariateLayer(
      fieldId: string,
      key: string,
    ): Promise<ManifestLayer> {
      return unwrap(
        await gw.apiGet<ManifestLayer>(
          `/fields/${fieldId}/covariates/layer`,
          {
            key,
          },
          { activeOrg: true },
        ),
      );
    },
    // CV3 — signed GET of the field's canonical CA1 stack GeoParquet. The FE decodes it
    // client-side to render ANY covariate column (lib/stackClient.ts). 404 = no stack. §A4.
    async fetchCovariateStack(fieldId: string): Promise<CovariateStackInfo> {
      return unwrap(
        await gw.apiGet<CovariateStackInfo>(
          `/fields/${fieldId}/covariates/stack`,
          {},
          { activeOrg: true },
        ),
      );
    },
    // Stored field lattice in keep order. Rx screens take their cells from here.
    async fetchFieldGrid(
      fieldId: string,
      opts?: { gridId?: string; fields?: "centroids" | "identity" },
    ): Promise<{
      boundary_version_id: string;
      cell_count: number;
      cell_order: "grid_keep";
      core_version: string;
      geometries: { type: string; coordinates: unknown }[];
      centroids: { lng: number; lat: number }[];
    }> {
      const params: Record<string, string> = {};
      if (opts?.gridId) params.grid_id = opts.gridId;
      if (opts?.fields) params.fields = opts.fields;
      const grid = unwrap(
        await gw.apiGet<{
          boundary_version_id: string;
          cell_count: number;
          cell_order: "grid_keep";
          core_version: string;
          geometries?: { type: string; coordinates: unknown }[] | null;
          centroids?: { lng: number; lat: number }[] | null;
        }>(`/fields/${fieldId}/grid`, params, { activeOrg: true }),
      );
      return {
        ...grid,
        geometries: grid.geometries ?? [],
        centroids: grid.centroids ?? [],
      };
    },
    // Named stack columns joined onto the field grid by cell_id. A column the
    // stack does not have is listed under `missing` and is not dropped.
    async fetchRxInputs(
      fieldId: string,
      keys: string[],
    ): Promise<{
      grid_id: string;
      columns: Record<string, unknown[]>;
      missing: { key: string; reason: "needs_covariate_build" }[];
    }> {
      return unwrap(
        await gw.apiGet(
          `/fields/${fieldId}/rx-inputs`,
          { keys: keys.join(",") },
          { activeOrg: true },
        ),
      );
    },
    // Per-field soil-type composition + clay-mineralogy artifact — DISPLAY-ONLY.
    async fetchSoilComposition(fieldId: string): Promise<FieldSoilComposition> {
      const data = unwrap(
        await gw.apiGet<FieldSoilComposition | SoilCompositionPointer>(
          `/fields/${fieldId}/soil-composition`,
          {},
          { activeOrg: true },
        ),
      );
      if ("signed_url" in data) {
        const res = await fetch(data.signed_url);
        if (!res.ok)
          throw new Error(
            `soil-composition blob download failed (${res.status})`,
          );
        return (await res.json()) as FieldSoilComposition;
      }
      return data;
    },
    async fetchSoilSamples(
      fieldId: string,
      opts: { property?: string; depth?: number; data_year?: number } = {},
    ): Promise<SoilSamplePoint[]> {
      return unwrap(
        await gw.apiGet<SoilSamplePoint[]>(
          `/fields/${fieldId}/soil-samples`,
          opts,
          { activeOrg: true },
        ),
      );
    },
    async fetchHaneyPoints(fieldId: string): Promise<HaneySamplePoint[]> {
      return unwrap(
        await gw.apiGet<HaneySamplePoint[]>(
          `/fields/${fieldId}/haney`,
          {},
          { activeOrg: true },
        ),
      );
    },
    async saveHaneyPoints(
      fieldId: string,
      points: HaneySamplePoint[],
    ): Promise<{ saved: number }> {
      return unwrap(
        await gw.apiPost<{ saved: number }>(
          `/fields/${fieldId}/haney`,
          {
            points,
          },
          { activeOrg: true },
        ),
      );
    },
    async fetchFertRxCoverage(): Promise<
      {
        field_id: string;
        nutrient: string;
        covers_through_year: number | null;
      }[]
    > {
      return unwrap(
        await gw.apiGet<
          {
            field_id: string;
            nutrient: string;
            covers_through_year: number | null;
          }[]
        >("/fertility-rx/coverage", {}, { activeOrg: true }),
      );
    },
    async fetchFertRx(fieldId: string, view?: "summary"): Promise<unknown[]> {
      return unwrap(
        await gw.apiGet<unknown[]>(
          `/fields/${fieldId}/fertility-rx`,
          view ? { view } : {},
          { activeOrg: true },
        ),
      );
    },
    async fetchFertRxById(fieldId: string, rxId: string): Promise<unknown> {
      return unwrap(
        await gw.apiGet<unknown>(
          `/fields/${fieldId}/fertility-rx/${rxId}`,
          {},
          { activeOrg: true },
        ),
      );
    },
    async saveFertRx(
      fieldId: string,
      rx: unknown,
    ): Promise<{ saved?: unknown }> {
      return unwrap(
        await gw.apiPost<{ saved?: unknown }>(
          `/fields/${fieldId}/fertility-rx`,
          { rx },
          { activeOrg: true },
        ),
      );
    },
    async deleteFertRx(
      fieldId: string,
      rxId: string,
    ): Promise<{ deleted?: boolean }> {
      return unwrap(
        await gw.apiDelete<{ deleted?: boolean }>(
          `/fields/${fieldId}/fertility-rx/${rxId}`,
          { activeOrg: true },
        ),
      );
    },
    async fetchRunningJobs(): Promise<JobRecord[]> {
      return unwrap(
        await gw.apiGet<JobRecord[]>(
          "/jobs",
          { status: "in_progress" },
          { activeOrg: true },
        ),
      );
    },
    async fetchJob(jobId: string): Promise<FieldJob> {
      return unwrap(
        await gw.apiGet<FieldJob>(`/jobs/${jobId}`, {}, { activeOrg: true }),
      );
    },

    // ---- Orders / Compute (POST to light -> dispatched to async) ------------
    async orderEcScan(fieldId: string, pattern: EcPattern): Promise<JobRecord> {
      return unwrap(
        await gw.apiPost<JobRecord>(
          `/fields/${fieldId}/ec-scan-requests`,
          {
            pattern,
          },
          { activeOrg: true },
        ),
      );
    },
    // The field's saved EC-scan orders (EC2 read-back) — merged into the Work orders page
    // beside the sampling orders. Same defensive normalization as fetchSamplingOrders.
    async fetchEcScanOrders(fieldId: string): Promise<EcScanOrder[]> {
      const rows = unwrap(
        await gw.apiGet<Record<string, unknown>[]>(
          `/fields/${fieldId}/ec-scan-requests`,
          {},
          { activeOrg: true },
        ),
      );
      if (!Array.isArray(rows)) return [];
      return rows.map((r, i) => ({
        request_id: String(
          r.ec_scan_request_id ?? r.request_id ?? r.id ?? `${fieldId}-ec-${i}`,
        ),
        status: String(r.status ?? "submitted"),
        created_at:
          typeof r.created_at === "string"
            ? r.created_at
            : typeof r.ordered_at === "string"
              ? r.ordered_at
              : null,
        pattern: typeof r.pattern === "string" ? r.pattern : null,
      }));
    },
    // Dispatch a covariate build (CV2) → async collects topo/NDVI/SSURGO (+ machine data
    // when present). Async dedupes on idempotency_key {field_id}:covariate_build, so
    // triggering it after boundary creation / machine-data import is safe. Front-End.md §A4.
    async buildCovariates(fieldId: string, set?: string): Promise<JobRecord> {
      return unwrap(
        await gw.apiPost<JobRecord>(
          `/fields/${fieldId}/covariates:build`,
          set ? { set } : {},
          { activeOrg: true },
        ),
      );
    },
    // Save a computed/drawn zone set (Z2 drawn path -> DB zone_store_drawn; small
    // geometry, direct write — no async parse). The FE's client-side Mahalanobis zone
    // design (lib/zoneDesign) saves through here; the stored zone_version becomes the
    // field's zones layer + a zones sample event (SS8).
    async saveZones(
      fieldId: string,
      geojson: unknown,
      cores?: { lng: number; lat: number; zone: number }[],
    ): Promise<{ zone_version_id?: string }> {
      const body: Record<string, unknown> = { geojson };
      if (cores?.length) body.cores = cores;
      const r = unwrap(
        await gw.apiPost<{ zone_version_id?: string } | null>(
          `/fields/${fieldId}/zones`,
          body,
          { activeOrg: true },
        ),
      );
      return r && typeof r === "object" ? r : {};
    },
    // Delete a field's saved zones: the "Target Sampling Layers" delete removes the saved zone set
    // so it stops surfacing as the `zones` layer.
    async deleteZones(fieldId: string): Promise<void> {
      unwrap(
        await gw.apiDelete<null>(`/fields/${fieldId}/zones`, {
          activeOrg: true,
        }),
      );
    },
    // Suggested management-zone COUNT for a field: the max zones such that adding one more would
    // drop a zone under the 4-acre floor.
    async suggestZoneCount(fieldId: string): Promise<{ zone_count: number }> {
      return unwrap(
        await gw.apiPost<{ zone_count: number }>(
          `/fields/${fieldId}/zones:suggest-count`,
          {},
          { activeOrg: true },
        ),
      );
    },
    // Lab pricing. The selected tier's price_per_acre is the rate; the caller
    // multiplies by acres so the total can update without another request.
    async quoteSampling(body: {
      field_id: string;
      config?: {
        acres?: number;
        point_count?: number;
        lab_id?: string;
        lab_name?: string;
        lab_shortname?: string;
        package_code?: string;
      };
    }): Promise<{
      selected_tier?: Record<string, unknown> | null;
      estimated_total?: number | null;
    }> {
      return unwrap(await gw.apiPost(`/soil-sampling/quote`, body));
    },
    async orderSampling(fieldId: string, config_: unknown): Promise<JobRecord> {
      return unwrap(
        await gw.apiPost<JobRecord>(
          `/fields/${fieldId}/soil-sampling-requests`,
          config_,
          { activeOrg: true },
        ),
      );
    },
    // }; the remaining fallbacks keep an unexpected shape degrading to placeholders, never a crash.
    async fetchSamplingOrders(fieldId: string): Promise<SamplingOrder[]> {
      const rows = unwrap(
        await gw.apiGet<Record<string, unknown>[]>(
          `/fields/${fieldId}/soil-sampling-requests`,
          {},
          { activeOrg: true },
        ),
      );
      if (!Array.isArray(rows)) return [];
      return rows.map((r, i) => samplingOrderFromWire(r, fieldId, i));
    },
    // Per-field data-source activity flags — LIVE, BE-light: GET returns the full merged map
    // (data-aware defaults + stored overrides); PATCH upserts one flag and returns the updated map.
    async fetchDataSources(fieldId: string): Promise<Record<string, boolean>> {
      return unwrap(
        await gw.apiGet<Record<string, boolean>>(
          `/fields/${fieldId}/data-sources`,
          {},
          { activeOrg: true },
        ),
      );
    },
    async patchDataSource(
      fieldId: string,
      key: string,
      active: boolean,
    ): Promise<Record<string, boolean>> {
      return unwrap(
        await gw.apiPatch<Record<string, boolean>>(
          `/fields/${fieldId}/data-sources`,
          { key, active },
          { activeOrg: true },
        ),
      );
    },
    // Sample events — the field's zones/points sampling campaigns (soil-samples flow
    // creates them; the map-soil-properties run picks one). §A5.
    /*
     *
     * Only the keys the user actually changed are sent — omitting one must leave it alone rather
     * than blanking it, which matters on a MIXED set where the user is fixing one field and not the
     * others.
     */
    async updateSampleProvenance(
      fieldId: string,
      body: {
        sample_event_id?: string | null;
        depth_in?: number;
        p_extractant?: string;
        k_extractant?: string;
        units?: string;
      },
    ): Promise<SampleProvenanceResult | null> {
      return unwrap(
        await gw.apiPatch<SampleProvenanceResult>(
          `/fields/${fieldId}/soil-samples:provenance`,
          body,
          { activeOrg: true },
        ),
      );
    },
    /*
     *
     * Fire-and-forget from the stores: the preference is already applied locally, so a failure
     * means "not synced yet" rather than lost work — but it is surfaced rather than swallowed
     * because a lens that silently differs per device is the failure this endpoint exists to
     * prevent.
     */
    async updateSoilUnits(patch: {
      depth_in?: number;
      p_extractant?: string;
      k_extractant?: string;
      mass_basis?: string;
      terrain_exaggeration?: number;
      enhanced_visuals?: boolean;
      ui_mode?: "easy" | "advanced";
      layer_detail?: "easy" | "medium" | "all";
    }): Promise<void> {
      await gw.apiPatch("/users/me/preferences", patch);
    },
    async notificationPreferences(): Promise<NotificationPreference[]> {
      const data = unwrap(
        await gw.apiGet<{ preferences: NotificationPreference[] }>(
          "/users/me/notification-preferences",
        ),
      );
      return data.preferences;
    },
    async updateNotificationPreferences(
      preferences: { category: NotificationCategory; enabled: boolean }[],
    ): Promise<NotificationPreference[]> {
      const data = unwrap(
        await gw.apiPut<{ preferences: NotificationPreference[] }>(
          "/users/me/notification-preferences",
          { preferences },
        ),
      );
      return data.preferences;
    },
    /*
     *
     * Extractant factors are served by the API, never hardcoded. Read from_m3 directly and never
     * invert to_m3: the display lens converts stored Mehlich-3 into the requested extractant, and a
     * guessed multiplier would disagree with the rest of the platform.
     */
    async extractantFactors(): Promise<ExtractantFactors> {
      return unwrap(
        await gw.apiGet<ExtractantFactors>(
          "/compute/extractant-factors",
          {},
          { activeOrg: true },
        ),
      );
    },
    /*
     *
     * The behavior-driver PERCENTILE thresholds, served rather than embedded — the 99x3 raw values
     * at the 1st-99th nationwide percentiles. Same reasoning as `extractantFactors`: two embedded
     * copies of one reference table is the duplication that has bitten this pair repeatedly.
     */
    async behaviorPercentiles(): Promise<{
      thresholds?: Record<string, number[]>;
    } | null> {
      return unwrap(
        await gw.apiGet<{ thresholds?: Record<string, number[]> }>(
          "/compute/behavior-percentiles",
          {},
          { activeOrg: true },
        ),
      );
    },
    /** One file from `GET /api/reference/{name}`. The browser caches it for a day. */
    async referenceDocument(name: string): Promise<Record<string, unknown>> {
      return unwrap(
        await gw.apiGet<Record<string, unknown>>(`/reference/${name}`),
      );
    },
    async fetchSampleEvents(fieldId: string): Promise<SampleEvent[]> {
      return unwrap(
        await gw.apiGet<SampleEvent[]>(
          `/fields/${fieldId}/sample-events`,
          {},
          { activeOrg: true },
        ),
      );
    },
    /*
     *
     * Merge N results-bearing POINT campaigns on one field into a single event (Visualize → Soil
     * samples). Irreversible. The server keeps the newest-dated campaign as the survivor, restamps
     * every absorbed point onto it (per-point `sampled_at` unchanged so the view date is the newest
     * automatically), and files depth-norm + relative covariates + the soil-map stale cascade.
     * Body is the selected ids, however many — not a pairwise fold.
     */
    async mergeSampleEvents(
      fieldId: string,
      eventIds: string[],
    ): Promise<SampleEventMergeResult> {
      const event_ids = [...new Set(eventIds.filter(Boolean))];
      if (event_ids.length < 2) {
        throw new ApiError(
          "ERR_VALIDATION",
          "Pick at least two sample events to merge.",
        );
      }
      return unwrap(
        await gw.apiPost<SampleEventMergeResult>(
          `/fields/${fieldId}/sample-events:merge`,
          { event_ids },
          { activeOrg: true },
        ),
      );
    },
    // The field carries a PINNED pointer: a pin sticks, so a newer campaign does NOT
    // auto-take-over. These are the two affordances that let a user change it — without them a
    // pinned field silently keeps fitting the older samples (BE-Light
    // `Front-End__no-ui-to-change-the current-sample-campaign`; live since `sm-api-read-00086`).
    async designateCurrentEvent(
      fieldId: string,
      eventId: string,
    ): Promise<{
      event_id?: string;
      is_current?: boolean;
      sample_date?: string | null;
      pinned?: boolean;
    }> {
      return unwrap(
        await gw.apiPost(
          `/fields/${fieldId}/sample-events/${encodeURIComponent(eventId)}:designate-current`,
          {},
          { activeOrg: true },
        ),
      );
    },
    // clear the pin → the server's view auto-picks the NEWEST results-bearing campaign again
    async resetCurrentEvent(fieldId: string): Promise<{ pinned?: boolean }> {
      return unwrap(
        await gw.apiPost(
          `/fields/${fieldId}/sample-events:reset-current`,
          {},
          { activeOrg: true },
        ),
      );
    },
    // A failed fetch throws. The work-in-progress pane hides on that error.
    async fetchOrgJobs(status?: string): Promise<OrgFieldJob[]> {
      return unwrap(
        await gw.apiGet<OrgFieldJob[]>(
          "/compute-jobs",
          status ? { status } : undefined,
          { activeOrg: true },
        ),
      );
    },
    async fetchFieldJobs(fieldId: string): Promise<FieldJob[]> {
      return unwrap(
        await gw.apiGet<FieldJob[]>(
          `/fields/${fieldId}/jobs`,
          {},
          { activeOrg: true },
        ),
      );
    },
    // Save a user-supplied sample POINT set (SS3/SS3b — small geometry, direct write).
    // Features whose properties carry measured values are batch-inserted into
    // soil_test_results server-side; the response says how many results landed.
    async saveTargetPoints(
      fieldId: string,
      body: {
        features: { type: "FeatureCollection"; features: unknown[] };
        id_column: string | null;
        source_filename: string | null;
      },
    ): Promise<{
      sample_event_id?: string;
      set_id: string;
      inserted: number;
      id_source: "file" | "ordinal";
      already_existed: boolean;
      layer_id: string;
    }> {
      return unwrap(
        await gw.apiPost(
          `/fields/${fieldId}/soil-sampling/target-points`,
          body,
          { activeOrg: true },
        ),
      );
    },
    async saveSamplePoints(
      fieldId: string,
      points: unknown,
    ): Promise<{
      sample_event_id?: string;
      results_inserted?: number;
      is_current?: boolean;
      sample_date?: string | null;
      normalization?: "done" | "queued" | null;
    }> {
      // is_current/sample_date — BE-Light `v_field_sample_events`: whether the just-saved event is
      // the field's current set or landed OLDER (→ the FE tells the user it's under "Old samples").
      return unwrap(
        await gw.apiPost<{
          sample_event_id?: string;
          results_inserted?: number;
          is_current?: boolean;
          sample_date?: string | null;
          normalization?: "done" | "queued" | null;
        }>(
          `/fields/${fieldId}/soil-sampling/points`,
          { points },
          { activeOrg: true },
        ),
      );
    },
    // Map soil properties (M1 dispatch). Default = full stack + trained AI models; opts
    // constrain the run to a user-picked covariate subset and/or manual weights (aligned
    // with layers) — the "Map soil properties" panel (Front-End.md §A6).
    async runModel(
      fieldId: string,
      properties: string[],
      opts: {
        layers?: string[];
        weights?: number[];
        sample_event_id?: string;
        method?: "idw" | "zones_flat" | "genius_zones";
        idw_power?: number;
        // Genius Zones: the corrected cores compute kriges, and the stack version they were read
        // from.
        cores?: {
          lng: number;
          lat: number;
          zone: number;
          values: Record<string, number | null>;
        }[];
        stack_content_version?: string;
      } = {},
    ): Promise<JobRecord> {
      const body: Record<string, unknown> = { properties };
      if (opts.layers?.length) body.layers = opts.layers;
      if (opts.weights?.length) body.weights = opts.weights;
      if (opts.sample_event_id) body.sample_event_id = opts.sample_event_id; // which sample event feeds the run
      // "Classic Grid (Purely Spatial)" school: interpolate the sample points with IDW (power=3)
      // instead of the ML ensemble.
      if (opts.method) body.method = opts.method;
      if (opts.idw_power != null) body.idw_power = opts.idw_power;
      if (opts.cores?.length) body.cores = opts.cores;
      if (opts.stack_content_version)
        body.stack_content_version = opts.stack_content_version;
      return unwrap(
        await gw.apiPost<JobRecord>(`/fields/${fieldId}/model:run`, body, {
          activeOrg: true,
        }),
      );
    },
    // Classic Grid. The API interpolates and stores the maps before it responds.
    async saveIdwMap(
      fieldId: string,
      sampleEventId: string | null,
      props?: string[],
    ): Promise<{
      status: string;
      properties: {
        property: string;
        raster_id: string;
        value_min: number | null;
        value_max: number | null;
      }[];
    }> {
      const body: Record<string, unknown> = {};
      if (sampleEventId) body.sample_event_id = sampleEventId;
      if (props?.length) body.props = props;
      return unwrap(
        await gw.apiPost(`/fields/${fieldId}/model/idw-map`, body, {
          activeOrg: true,
        }),
      );
    },

    // ---- John Deere (Front-End.md §A1) — machine LAYERS, never files ----------
    // JD10 — the linked field's MACHINE LAYERS (operations). Defensive key mapping like
    // the other JD reads; raw JD payloads often carry NO human name (only GUIDs), so a
    // readable label is SYNTHESIZED from kind + crop/product + season ("Fall '25 harvest
    // (corn)") — never show an opaque id to the user.
    async jdOperations(fieldId: string): Promise<JdOperation[]> {
      const raw = unwrap(
        await gw.apiGet<Record<string, unknown>[]>(
          `/fields/${fieldId}/johndeere/operations`,
          {},
          { activeOrg: true },
        ),
      );
      return (raw ?? []).map((o) => {
        const operation_id = pickStr(o, ["operation_id", "id", "operationId"]);
        const kind = (
          pickStr(o, ["kind", "operation_type", "operationType", "type"]) ||
          "operation"
        ).toLowerCase();
        const date =
          pickStr(o, [
            "date",
            "start_date",
            "startDate",
            "end_date",
            "endDate",
          ]) || undefined;
        const explicit = pickStr(o, ["label", "name", "title"]);
        return {
          operation_id,
          kind,
          label:
            explicit && !looksLikeId(explicit)
              ? explicit
              : jdOpLabel(o, kind, date),
          date,
          has_file: Boolean(o.has_file),
          // WHICH JD field this layer came from — JD10 now lists across ALL of the field's links.
          jd_org_id: pickStr(o, ["jd_org_id"]) || undefined,
          jd_field_id: pickStr(o, ["jd_field_id"]) || undefined,
        };
      });
    },
    // Pull machine LAYERS into the field (never files — the backend resolves/generates the JD
    // export per operation and tracks the op<->file mapping). mode:'yield' = all yield layers
    // mode:'all' = everything; operation_ids = an explicit selection.
    async jdPullOperations(
      fieldId: string,
      scope: { mode: "yield" | "all" } | { operation_ids: string[] },
    ): Promise<
      JobRecord & {
        job_ids?: string[];
        jd_field_count?: number;
        operation_count?: number;
      }
    > {
      return unwrap(
        await gw.apiPost(
          `/fields/${fieldId}/johndeere/operations:pull`,
          scope,
          { activeOrg: true },
        ),
      );
    },

    // ---- Uploads: mint signed PUT URL, then client -> GCS (Front-End.md §C) --
    async mintUpload(
      fieldId: string,
      kind: "soil-samples" | "boundary" | "zones",
      contentType: string,
      opts: {
        sample_event_id?: string;
        filename?: string;
        work_order_id?: string;
      } = {},
    ): Promise<SignedUpload> {
      // `filename`: the file's original name rides to compute so the version is called "Imported -
      // Home_40_zones" instead of "Imported - Uploaded zones <date>"; never the path.
      if (kind === "zones") {
        const env = await gw.apiPost<SignedUpload>(
          `/fields/${fieldId}/zones`,
          {
            file_content_type: contentType,
            ...(opts.filename ? { filename: opts.filename } : {}),
          },
          { activeOrg: true },
        );
        return { ...unwrap(env), job_id: env.meta?.job_id ?? null };
      }
      // sample_event_id (SS7) attributes parsed lab results to a sample event so its
      // has_results flips — without it results parse but flip nothing (SS8, honest).
      const body: Record<string, unknown> = { content_type: contentType };
      if (opts.sample_event_id) body.sample_event_id = opts.sample_event_id;
      if (opts.work_order_id) body.work_order_id = opts.work_order_id;
      const env = await gw.apiPost<SignedUpload>(
        `/fields/${fieldId}/${kind}:upload`,
        body,
        { activeOrg: true },
      );
      return { ...unwrap(env), job_id: env.meta?.job_id ?? null };
    },
    async uploadFile(
      fieldId: string,
      kind: "soil-samples" | "boundary" | "zones",
      file: File,
      opts: { sample_event_id?: string; work_order_id?: string } = {},
    ) {
      const contentType = contentTypeOf(file);
      const mint = await this.mintUpload(fieldId, kind, contentType, {
        ...opts,
        filename: file.name,
      });
      await gw.uploadToSignedUrl(mint.signed_put_url, file, contentType);
      return mint;
    },

    // ---- Bulk ingest: `:begin` era (BE-Light rev 00106+; mint 404s, :start 405s) ----
    /*
     *
     * ONE call at press: files the whole job chain (incl. the cleanup finalizer) BEFORE any byte can
     * exist, then returns ~120 s signed URLs for exactly that manifest. If this throws, NOTHING was
     * uploaded anywhere -- that ordering is the entire orphaned-byte design.
     */
    /*
     *
     * `sample_defaults` travels WITH the drop rather than living in the browser: it is how a sample
     * without its own depth/extractant gets interpreted, so it has to be readable by whatever
     * imports the file, hours later and on another machine. The FE requires all three before it
     * will start an upload, so this is either complete or absent -- never partial.
     */
    async beginBulkUpload(
      // `fe_yield`: the FE parsed this harvest export at press time and ATTESTS what it found —
      // BE-Light relays the envelope VERBATIM into compute's `attach_yield` plan (any reshaping
      // would make a divergence unattributable) and passes `field_id` as the proposal compute
      // attests but never obeys.
      files: {
        filename: string;
        content_type?: string;
        fe_attached?: boolean;
        fe_yield?: { envelope: unknown; field_id?: string };
      }[],
      sampleDefaults?: SampleDefaults,
      sampleOptions?: {
        may_contain_samples: boolean;
        match_by_point_id: boolean;
      },
      feCreatedFieldIds?: string[],
    ): Promise<BulkBeginResult> {
      const body = {
        files,
        source: "upload",
        ...(sampleDefaults ? { sample_defaults: sampleDefaults } : {}),
        // sample-intent flags: explicit opt-in gates ALL sample ingestion, and the point-ID join is
        // a second explicit opt-in on top (off = never join, avoid mixed-up samples).
        ...(sampleOptions ? sampleOptions : {}),
        // FIELD FAST PATH: fields this drop's boundaries were ALREADY created as client-side
        // before upload — the chain's dedup resolves matching candidates to them silently instead
        // of filing overlap errors (BE-Light threads it into the plan params request filed). Sent
        // only when non-empty.
        ...(feCreatedFieldIds?.length
          ? { fe_created_field_ids: feCreatedFieldIds }
          : {}),
      };
      try {
        return unwrap(
          await gw.apiPost<BulkBeginResult>(
            "/bulk-ingest/sessions:begin",
            body,
            { activeOrg: true },
          ),
        );
      } catch (e) {
        // an OLDER BE-Light that rejects the unknown param must never block the drop — retry once
        // without it (the fields exist either way; only the error-entry silencing is lost).
        if (
          feCreatedFieldIds?.length &&
          e instanceof ApiError &&
          /fe_created_field_ids/.test(e.message)
        ) {
          const { fe_created_field_ids: _dropped, ...rest } = body as Record<
            string,
            unknown
          >;
          return unwrap(
            await gw.apiPost<BulkBeginResult>(
              "/bulk-ingest/sessions:begin",
              rest,
              { activeOrg: true },
            ),
          );
        }
        // NEVER strip `fe_yield` to retry. BE-Light refuses a lane that cannot be filed with a 503
        // that does NOT contain the string "fe_yield" (the whole press fails, nothing minted).
        if (
          e instanceof ApiError &&
          /fe_attached/.test(e.message) &&
          files.some((f) => f.fe_attached)
        ) {
          throw new ApiError(
            e.code,
            `${e.message} — this server doesn't accept the real-time-attach annotation; retrying without it would duplicate the attached samples`,
            e.details,
          );
        }
        throw e;
      }
    },
    /*
     *
     * JD source (BE-Light rev 00111): send ids (+names -- they're on screen and save compute a naming
     * heuristic); BE-Light fetches each boundary from JD SERVER-SIDE during:begin (the boundary is a
     * separate JD resource only they hold credentials for). No bytes, no URLs, no upload phase.
     */
    async beginBulkJd(
      jdOrgId: string,
      fields: { jd_field_id: string; name?: string }[],
    ): Promise<BulkBeginResult> {
      return unwrap(
        await gw.apiPost<BulkBeginResult>(
          "/bulk-ingest/sessions:begin",
          {
            source: "johndeere",
            jd_org_id: jdOrgId,
            jd_fields: fields,
          },
          { activeOrg: true },
        ),
      );
    },
    /* * Re-issue URLs for paths ALREADY in the session's manifest (the seal blocks new FILES, not new URLs). */
    async remintBulkUploads(
      sessionId: string,
      objectPaths: string[],
    ): Promise<BulkBeginResult["files"]> {
      const r = unwrap(
        await gw.apiPost<{
          session_id: string;
          files: BulkBeginResult["files"];
        }>(
          `/bulk-ingest/sessions/${sessionId}/uploads:remint`,
          {
            object_paths: objectPaths,
          },
          { activeOrg: true },
        ),
      );
      return r.files;
    },
    /*
     *
     * The whole press: begin -> PUT every file (bounded concurrency) -> report. The 120 s TTL means a slow
     * upload 403s AS THE NORMAL PATH, not an edge case -- on a failed PUT we re-mint that path once and
     * retry before declaring the file failed. Failed files are surfaced by the caller ("didn't reach us"):
     * extraction will independently error them as not-arrived, and their bytes die with the session's own
     * cleanup either way.
     */
    async uploadBulkDrop(
      files: File[],
      opts: {
        onFileDone?: (filename: string, ok: boolean) => void;
        sampleDefaults?: SampleDefaults;
        sampleOptions?: {
          may_contain_samples: boolean;
          match_by_point_id: boolean;
        };
        feCreatedFieldIds?: string[];
        feAttachedKeys?: ReadonlySet<string>;
        feYieldByKey?: ReadonlyMap<
          string,
          { envelope: unknown; field_id?: string }
        >;
      } = {},
    ): Promise<{
      session_id: string;
      job_id: string;
      uploaded: { object_path: string; filename: string }[];
      failed: { filename: string; error: string }[];
      /*
       * fileKey (`name:size`) → the `yield_attach` job id, only for files whose annotation was
       * accepted AND whose bytes uploaded. Absent = do not tell the grower this file is on the fast lane.
       */
      yieldJobs: Record<string, string>;
      /* * BL-Y5 answers for each annotated PUT that committed. Empty when the wait failed — preview stays. */
      yieldCommits: YieldCommitResult[];
    }> {
      const begin = await this.beginBulkUpload(
        // fe_attached rides per FILE, matched by name:size (the manifest's identity — names collide)
        files.map((f) => {
          const key = `${f.name}:${f.size}`;
          const yieldAttestation = opts.feYieldByKey?.get(key);
          return {
            filename: f.name,
            content_type: contentTypeOf(f),
            ...(opts.feAttachedKeys?.has(key) ? { fe_attached: true } : {}),
            // gated: a missing attach_yield plan 503s the WHOLE drop.
            ...(yieldAttachIsLive() && yieldAttestation
              ? { fe_yield: yieldAttestation }
              : {}),
          };
        }),
        opts.sampleDefaults,
        opts.sampleOptions,
        opts.feCreatedFieldIds,
      );
      // pair minted slots to Files by POSITION -- two dropped files can legitimately share a name
      const queue = begin.files
        .map((m, j) => ({ m, file: files[j] }))
        .filter((p) => p.file);
      const uploaded: { object_path: string; filename: string }[] = [];
      const failed: { filename: string; error: string }[] = [];
      const yieldCommits: YieldCommitResult[] = [];
      let next = 0;
      const worker = async () => {
        for (;;) {
          const idx = next++;
          if (idx >= queue.length) return;
          const { m, file } = queue[idx];
          try {
            try {
              await gw.uploadToSignedUrl(
                m.signed_put_url,
                file,
                m.content_type,
              );
            } catch {
              // expired-or-failed URL: re-mint THIS path and retry once (the seal allows re-mints for
              // manifested paths; only new filenames 422)
              const fresh = await this.remintBulkUploads(begin.session_id, [
                m.object_path,
              ]);
              const slot = fresh.find((f) => f.object_path === m.object_path);
              if (!slot) throw new Error("re-mint did not return this file");
              await gw.uploadToSignedUrl(
                slot.signed_put_url,
                file,
                slot.content_type ?? m.content_type,
              );
            }
            uploaded.push({ object_path: m.object_path, filename: m.filename });
            // BL-Y5: wait on this file's already-filed lane. Proposal field in the URL; bulk
            // object paths are accepted. A failed wait must not fail the PUT that already landed.
            const att = opts.feYieldByKey?.get(`${file.name}:${file.size}`);
            const yieldJobId = m.yield_job_id;
            if (yieldJobId && att?.field_id) {
              try {
                yieldCommits.push(
                  await this.commitFieldYield(att.field_id, {
                    object_path: m.object_path,
                    job_id: yieldJobId,
                  }),
                );
              } catch {
                /* lane still running or door missing — preview stays; BL-Y4 still runs */
              }
            }
            opts.onFileDone?.(m.filename, true);
          } catch (e) {
            failed.push({
              filename: m.filename,
              error: e instanceof Error ? e.message : String(e),
            });
            opts.onFileDone?.(m.filename, false);
          }
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(4, queue.length) }, worker),
      );
      const uploadedNames = new Set(uploaded.map((u) => u.filename));
      const yieldJobs: Record<string, string> = {};
      for (const [j, f] of files.entries()) {
        const jobId = begin.files[j]?.yield_job_id;
        if (!jobId || !uploadedNames.has(f.name)) continue;
        yieldJobs[`${f.name}:${f.size}`] = jobId;
      }
      return {
        session_id: begin.session_id,
        job_id: begin.job_id,
        uploaded,
        failed,
        yieldJobs,
        yieldCommits,
      };
    },

    // ---- Bulk ingest: the Errors menu (append-only; render from the ROW, never fetch the session) ----
    async bulkErrors(
      params: {
        session_id?: string;
        source?: string;
        reason_code?: string;
        limit?: number;
        cursor?: string;
      } = {},
    ): Promise<{
      rows: BulkErrorRow[];
      total: number;
      next_cursor: string | null;
    }> {
      const env = await gw.apiGet<{
        errors: BulkErrorRow[];
        next_cursor?: string | null;
      }>("/bulk-ingest/errors", params, { activeOrg: true });
      const data = unwrap(env);
      return {
        rows: data.errors ?? [],
        total:
          typeof env.meta?.total === "number"
            ? env.meta.total
            : (data.errors ?? []).length,
        next_cursor: data.next_cursor ?? null,
      };
    },
    /* * Badge count. `count: null` = ABSENT (render nothing) -- never a confident 0 from an unreadable store. */
    async bulkErrorsCount(): Promise<BulkErrorBadge> {
      return unwrap(
        await gw.apiGet<BulkErrorBadge>(
          "/bulk-ingest/errors:count",
          {},
          { activeOrg: true },
        ),
      );
    },
    /* * A monotonic BOOKMARK, not a dismissal: touches no error row; repeat/out-of-order calls are safe. */
    async bulkErrorsMarkSeen(): Promise<void> {
      await gw.apiPost(
        "/bulk-ingest/errors:mark-seen",
        {},
        { activeOrg: true },
      );
    },

    // ---- Bulk ingest: session relay reads (BE-Light rev sm-api-read-00103-xxv) --
    // Two of BE-Light's semantics matter more than the shapes, and both are easy to destroy by being
    // "helpful" (their words: please don't fix them):
    // 404 = the drop hasn't been STARTED yet. We mint the session id before compute creates the row, so
    // that window is normal. It also covers someone else's session — answering there would read
    // as "nothing to classify" rather than "not yours".
    // 503 = the store is unreadable. It must NEVER become. An empty list MEANS something here ("no
    // files failed", "nothing left to classify"), so a swallowed error walks the user straight past
    // the decisions this whole flow exists to collect.
    // So: nothing below catches. `bulkSession` returns null ONLY for a genuine not-found; every other
    // failure propagates.
    async bulkSessions(
      params: { status?: string; limit?: number } = {},
    ): Promise<BulkSessionRow[]> {
      return unwrap(
        await gw.apiGet<BulkSessionRow[]>("/bulk-ingest/sessions", params, {
          activeOrg: true,
        }),
      );
    },
    /* * null = not started yet (or not ours). Any other failure throws — see the 503 note above. */
    async bulkSession(sessionId: string): Promise<BulkSessionRow | null> {
      try {
        return unwrap(
          await gw.apiGet<BulkSessionRow>(
            `/bulk-ingest/sessions/${sessionId}`,
            {},
            { activeOrg: true },
          ),
        );
      } catch (e) {
        if (e instanceof ApiError && e.code === "ERR_NOT_FOUND") return null;
        throw e;
      }
    },
    /*
     *
     * Returns the page AND `meta.total` (the pre-paging `count(*) OVER `), because a drop can hold
     * thousands of objects and "showing 200 of 4,812" is the difference between a page and a lie.
     */
    async bulkObjects(
      sessionId: string,
      params: {
        class?: string;
        disposition?: string;
        file_id?: string;
        needs_date_only?: boolean;
        include_geometry?: boolean;
        limit?: number;
        offset?: number;
      } = {},
    ): Promise<{ rows: BulkObjectRow[]; total: number }> {
      // limit caps at 2000 server-side; ask for what we'll draw rather than relying on the ceiling.
      const env = await gw.apiGet<BulkObjectRow[]>(
        `/bulk-ingest/sessions/${sessionId}/objects`,
        params,
        { activeOrg: true },
      );
      const rows = unwrap(env);
      // `meta.total` is absent on the single-session read but present on collections; fall back to the page
      // length rather than 0, so a missing total never renders as "0 objects" over a non-empty page.
      return {
        rows,
        total:
          typeof env.meta?.total === "number" ? env.meta.total : rows.length,
      };
    },
    async bulkConflicts(
      sessionId: string,
      params: { pending_only?: boolean } = {},
    ): Promise<BulkConflictRow[]> {
      return unwrap(
        await gw.apiGet<BulkConflictRow[]>(
          `/bulk-ingest/sessions/${sessionId}/conflicts`,
          params,
          { activeOrg: true },
        ),
      );
    },

    // Never add a 404 branch here. Keys are CASE-SENSITIVE server-side (whitespace stripped, case
    // preserved) -- `lib/flagDismissals` `dismissKey` is the ONLY place a key is built, which is
    // what keeps that from mattering.
    async listFlagDismissals(): Promise<Record<string, string[]>> {
      return unwrap(
        await gw.apiGet<Record<string, string[]>>(
          "/flag-dismissals",
          {},
          { activeOrg: true },
        ),
      );
    },
    async addFlagDismissal(fieldId: string, key: string): Promise<void> {
      await gw.apiPut(
        `/fields/${fieldId}/flag-dismissals/${encodeURIComponent(key)}`,
        {},
        { activeOrg: true },
      );
    },
    /* * `was_dismissed` = whether this call actually removed a row (false = it was already visible). */
    async removeFlagDismissal(
      fieldId: string,
      key: string,
    ): Promise<{ field_id: string; flag_key: string; was_dismissed: boolean }> {
      return unwrap(
        await gw.apiDelete<{
          field_id: string;
          flag_key: string;
          was_dismissed: boolean;
        }>(`/fields/${fieldId}/flag-dismissals/${encodeURIComponent(key)}`, {
          activeOrg: true,
        }),
      );
    },

    // `is_global_admin` is read from the DB per request on their side and is NEVER a JWT claim --
    // so revoking admin bites on the next request rather than whenever a token happens to expire.
    // NOT org-scoped by design: a global admin whose active org is one farm must still see the
    // system.
    async fetchSystemCounts(): Promise<SystemCounts> {
      return unwrap(
        await gw.apiGet<SystemCounts>(
          "/admin/system/counts",
          {},
          { activeOrg: true },
        ),
      );
    },
    async fetchAdminUsers(
      params: {
        org_id?: string;
        q?: string;
        limit?: number;
        offset?: number;
      } = {},
    ): Promise<{
      users: AdminUserRow[];
    }> {
      const raw = unwrap(
        await gw.apiGet<unknown>("/admin/system/users", params, {
          activeOrg: true,
        }),
      );
      return { users: asList<AdminUserRow>(raw, "users", "items") };
    },
    async fetchAdminApiKeys(): Promise<AdminApiKeyRow[]> {
      return asList<AdminApiKeyRow>(
        unwrap(
          await gw.apiGet<unknown>(
            "/admin/system/api-keys",
            {},
            { activeOrg: true },
          ),
        ),
        "keys",
        "items",
      );
    },
    async fetchAdminApiScopes(): Promise<AdminApiScope[]> {
      return asList<AdminApiScope>(
        unwrap(
          await gw.apiGet<unknown>(
            "/admin/system/api-keys/scopes",
            {},
            { activeOrg: true },
          ),
        ),
        "scopes",
        "items",
      );
    },
    async fetchAdminApiKeyImpact(
      keyId: string,
      orgId?: string,
    ): Promise<AdminApiKeyImpact> {
      return unwrap(
        await gw.apiGet<AdminApiKeyImpact>(
          `/admin/system/api-keys/${keyId}/impact`,
          orgId ? { org_id: orgId } : {},
          { activeOrg: true },
        ),
      );
    },
    async createAdminApiKey(body: {
      name: string;
      scopes: string[];
      org_ids: string[];
      max_orders_per_day: number;
    }): Promise<AdminApiKeyIssued> {
      return unwrap(
        await gw.apiPost<AdminApiKeyIssued>("/admin/system/api-keys", body, {
          activeOrg: true,
        }),
      );
    },
    async rotateAdminApiKey(keyId: string): Promise<AdminApiKeyIssued> {
      return unwrap(
        await gw.apiPost<AdminApiKeyIssued>(
          `/admin/system/api-keys/${keyId}/rotate`,
          {},
          { activeOrg: true },
        ),
      );
    },
    async revokeAdminApiKey(
      keyId: string,
    ): Promise<{ key_id: string; revoked: boolean }> {
      return unwrap(
        await gw.apiDelete<{ key_id: string; revoked: boolean }>(
          `/admin/system/api-keys/${keyId}`,
          { activeOrg: true },
        ),
      );
    },
    async linkAdminApiKeyOrg(
      keyId: string,
      orgId: string,
    ): Promise<{ key_id: string; org_id: string }> {
      return unwrap(
        await gw.apiPost<{ key_id: string; org_id: string }>(
          `/admin/system/api-keys/${keyId}/orgs`,
          { org_id: orgId },
          { activeOrg: true },
        ),
      );
    },
    async unlinkAdminApiKeyOrg(
      keyId: string,
      orgId: string,
    ): Promise<{ key_id: string; org_id: string; removed: boolean }> {
      return unwrap(
        await gw.apiDelete<{
          key_id: string;
          org_id: string;
          removed: boolean;
        }>(`/admin/system/api-keys/${keyId}/orgs/${orgId}`, {
          activeOrg: true,
        }),
      );
    },
    async setAdminApiKeyScopes(
      keyId: string,
      scopes: string[],
    ): Promise<{ key_id: string; scopes: string[] }> {
      return unwrap(
        await gw.apiPut<{ key_id: string; scopes: string[] }>(
          `/admin/system/api-keys/${keyId}/scopes`,
          { scopes },
          { activeOrg: true },
        ),
      );
    },
    /*
     * Manager state — ALSO carries the live VM count at `capacity.observed` (compute's /reconcile
     * stamps it every 5 min; there is deliberately no VM endpoint).
     */
    /*
     * Every org in the system (+ member/field counts) -- the users filter must span orgs the admin
     * does not belong to, which `orgStore` (their own memberships) cannot provide.
     */
    async fetchAdminOrgs(): Promise<AdminOrgRow[]> {
      return asList<AdminOrgRow>(
        unwrap(
          await gw.apiGet<unknown>(
            "/admin/system/orgs",
            {},
            { activeOrg: true },
          ),
        ),
        "orgs",
        "organizations",
        "items",
      );
    },
    async fetchOpsManager(): Promise<OpsManagerState> {
      return unwrap(
        await gw.apiGet<OpsManagerState>(
          "/admin/ops/manager-state",
          {},
          { activeOrg: true },
        ),
      );
    },
    async fetchOpsProcesses(): Promise<OpsProcess[]> {
      return asList<OpsProcess>(
        unwrap(
          await gw.apiGet<unknown>(
            "/admin/ops/processes",
            {},
            { activeOrg: true },
          ),
        ),
        "processes",
        "items",
      );
    },
    /*
     *
     * All active jobs across EVERY org (the org-scoped `/compute-jobs` can't serve a system view).
     * BE-Light serves the list ALONGSIDE counts-by-status, so the payload is an object -- normalised here.
     */
    /*
     *
     * Why this takes a `view` and not a status: before 00118 the FE asked `?
     */
    async fetchAdminJobs(
      view: "active" | "recent" = "active",
    ): Promise<OrgFieldJob[]> {
      const raw = unwrap(
        await gw.apiGet<unknown>("/admin/ops/jobs", {}, { activeOrg: true }),
      );
      // a bare array (older revision) is still the ACTIVE list; `recent` simply doesn't exist there
      if (Array.isArray(raw))
        return view === "active" ? (raw as OrgFieldJob[]) : [];
      return asList<OrgFieldJob>(
        raw,
        view,
        view === "active" ? "jobs" : "completed",
        "items",
      );
    },

    // NOTE: no fast-tier client methods remain — point suggestion moved CLIENT-SIDE —
    // lib/samplingDesign, -directed methodology and predict-points has no UI yet.
  };
}

export type Client = ReturnType<typeof makeClient>;
